import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Calendar,
  MapPin,
  Upload,
  X,
  Plus,
  ChevronLeft,
  Loader2,
  Clock,
  Users,
  Tag,
  Globe,
} from 'lucide-react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import { cn } from '@utils';
import api from '@services/api';

const eventSchema = z.object({
  name: z.string().min(3, 'Event name must be at least 3 characters'),
  description: z.string().optional(),
  type: z.enum(['single', 'recurring', 'multiday']),
  start_date: z.string().min(1, 'Start date is required'),
  end_date: z.string().optional(),
  venue_id: z.string().optional(),
  capacity: z.number().min(1, 'Capacity must be at least 1'),
  currency: z.string().default('USD'),
  is_featured: z.boolean().default(false),
  is_private: z.boolean().default(false),
});

type EventFormData = z.infer<typeof eventSchema>;

const steps = [
  { id: 1, title: 'Basic Info', description: 'Event details' },
  { id: 2, title: 'Tickets', description: 'Ticket types' },
  { id: 3, title: 'Settings', description: 'Advanced options' },
];

export default function EventCreatePage() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [featuredImage, setFeaturedImage] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<EventFormData>({
    resolver: zodResolver(eventSchema),
    defaultValues: {
      type: 'single',
      currency: 'USD',
      capacity: 100,
      is_featured: false,
      is_private: false,
    },
  });

  const onSubmit = async (data: EventFormData) => {
    setIsSubmitting(true);
    try {
      const response = await api.post<{ data: { id: number } }>('/events', data);
      toast.success('Event created successfully!');
      navigate(`/events/${response.data.id}`);
    } catch (error: any) {
      toast.error(error.message || 'Failed to create event');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFeaturedImage(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/events" className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800">
          <ChevronLeft className="w-5 h-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Create Event</h1>
          <p className="text-gray-500 dark:text-gray-400">Set up your new event</p>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center justify-between">
        {steps.map((step, index) => (
          <div key={step.id} className="flex items-center flex-1">
            <div className="flex flex-col items-center">
              <div
                className={cn(
                  'w-10 h-10 rounded-full flex items-center justify-center font-medium',
                  currentStep >= step.id
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                )}
              >
                {step.id}
              </div>
              <div className="mt-2 text-center">
                <p className="text-sm font-medium text-gray-900 dark:text-white">{step.title}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{step.description}</p>
              </div>
            </div>
            {index < steps.length - 1 && (
              <div
                className={cn(
                  'flex-1 h-0.5 mx-4',
                  currentStep > step.id
                    ? 'bg-primary-600'
                    : 'bg-gray-200 dark:bg-gray-700'
                )}
              />
            )}
          </div>
        ))}
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {currentStep === 1 && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="card p-6 space-y-6"
          >
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Basic Information</h2>

            {/* Featured Image */}
            <div>
              <label className="label">Featured Image</label>
              <div className="relative">
                {featuredImage ? (
                  <div className="relative w-full h-48 rounded-lg overflow-hidden">
                    <img src={featuredImage} alt="Featured" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => setFeaturedImage(null)}
                      className="absolute top-2 right-2 p-2 bg-white rounded-lg shadow-lg hover:bg-gray-100"
                      aria-label="Remove Image"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-primary-500 dark:border-gray-600 dark:hover:border-primary-400">
                    <Upload className="w-8 h-8 text-gray-400 mb-2" />
                    <span className="text-sm text-gray-500">Click to upload image</span>
                    <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
                  </label>
                )}
              </div>
            </div>

            {/* Event Name */}
            <div>
              <label htmlFor="name" className="label">
                Event Name *
              </label>
              <input
                {...register('name')}
                type="text"
                id="name"
                placeholder="e.g., Summer Music Festival"
                className={cn('input', errors.name && 'input-error')}
              />
              {errors.name && <p className="mt-1 text-sm text-danger-600">{errors.name.message}</p>}
            </div>

            {/* Description */}
            <div>
              <label htmlFor="description" className="label">
                Description
              </label>
              <textarea
                {...register('description')}
                id="description"
                rows={4}
                placeholder="Describe your event..."
                className="input"
              />
            </div>

            {/* Event Type */}
            <div>
              <label className="label">Event Type</label>
              <div className="grid grid-cols-3 gap-4">
                {['single', 'recurring', 'multiday'].map((type) => (
                  <label
                    key={type}
                    className={cn(
                      'flex flex-col items-center p-4 border-2 rounded-lg cursor-pointer transition-colors',
                      watch('type') === type
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                        : 'border-gray-200 hover:border-gray-300 dark:border-gray-700'
                    )}
                  >
                    <input {...register('type')} type="radio" value={type} className="sr-only" />
                    <Calendar className="w-6 h-6 mb-2 text-gray-500" />
                    <span className="text-sm font-medium capitalize">{type}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Date & Time */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="start_date" className="label">
                  Start Date *
                </label>
                <input
                  {...register('start_date')}
                  type="datetime-local"
                  id="start_date"
                  className={cn('input', errors.start_date && 'input-error')}
                />
                {errors.start_date && <p className="mt-1 text-sm text-danger-600">{errors.start_date.message}</p>}
              </div>
              <div>
                <label htmlFor="end_date" className="label">
                  End Date
                </label>
                <input {...register('end_date')} type="datetime-local" id="end_date" className="input" />
              </div>
            </div>

            {/* Capacity */}
            <div>
              <label htmlFor="capacity" className="label">
                Capacity *
              </label>
              <input
                {...register('capacity', { valueAsNumber: true })}
                type="number"
                id="capacity"
                min={1}
                className={cn('input', errors.capacity && 'input-error')}
              />
              {errors.capacity && <p className="mt-1 text-sm text-danger-600">{errors.capacity.message}</p>}
            </div>
          </motion.div>
        )}

        {/* Navigation Buttons */}
        <div className="flex justify-between">
          {currentStep > 1 ? (
            <button
              type="button"
              onClick={() => setCurrentStep(currentStep - 1)}
              className="btn-secondary"
            >
              Previous
            </button>
          ) : (
            <Link to="/events" className="btn-secondary">
              Cancel
            </Link>
          )}

          {currentStep < steps.length ? (
            <button
              type="button"
              onClick={() => setCurrentStep(currentStep + 1)}
              className="btn-primary"
            >
              Continue
            </button>
          ) : (
            <button type="submit" disabled={isSubmitting} className="btn-primary">
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Event'
              )}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
